import React, { useState, useRef } from 'react';
import clsx from 'clsx';
import { isEmpty } from 'lodash';

import Form, { FormSubmit } from 'components/Form';
import InputField from 'components/FormFields/InputField';
import FormPassword from 'components/FormFields/FormPassword';

import logo from 'assets/images/logo-color.png';

import './signin.scss';

// eslint-disable-next-line no-unused-vars
const SignIn = (props) => {
  const { handleSignIn, handleResetPassword, setOpenSignInModel, signInMessage, signInTitle } = props;

  const usernameRef = useRef();
  const passwordRef = useRef();

  const [resetMode, setResetMode] = useState(false);
  const [resetUsername, setResetUsername] = useState('');
  const [resetError, setResetError] = useState([]);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [accaontIsLocked, setAccaontIsLocked] = useState(false);

  const [submitDisabled, setSubmitDisabled] = useState(!handleSignIn);
  const [error, setError] = useState('');
  const hasError = !!error && error !== '';

  const handleFormError = (msg = '') => {
    setError(msg);
    setSubmitDisabled(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!usernameRef.current || !passwordRef.current) {
      // Throw error message to refresh.
      return;
    }

    const username = usernameRef.current.value;
    const password = passwordRef.current.value;
    if (!username) {
      usernameRef.current.focus();
      return;
    }

    if (!password) {
      passwordRef.current.focus();
      return;
    }
    setSubmitDisabled(true);
    const response = await handleSignIn(username, password);
    if (response.ok) {
      const { data } = response;
      if (data.ACCOUNT_CLOSED){
        handleFormError('Your account was closed, please contact support');
      } else if (data.ACCOUNT_PLACEHOLDER) { // 1
        handleFormError('You need to accept your email invite before logging in');
      } else if (data.EMAIL_NOT_VERIFIED) {
        handleFormError(`It looks like you haven't verified your account just yet!
          Please check your email inbox and verify your account.`);
      } else if (data.NEEDS_PASSWORD_RESET) {
        handleFormError('The password for this account needs to be reset.');
      } else if (data.SIGNIN_FAIL) {// 2
        handleFormError(`We can’t seem to find that email and password combination in our records.
          Forgot your password? Reset it below.`);
      } else if (data.CREATE_LINKED_LOGINS_ERROR) {
        handleFormError('You can only link pro accounts at this time');
      } else if (data.PASSWORD_WAIT) { // 3, 4, 5
        handleFormError(`For security purposes, please wait ${data.PASSWORD_WAIT}
          ${data.PASSWORD_WAIT === 1 ? ' minute' : ' minutes'} before attempting
          to sign in again, or reset your password below.`);
      } else if (data.ACCOUNT_LOCKED) { // 6
        handleFormError(`You have been locked out of your account for too many failed login attempts.
        To access your account, reset your password below.`);
        setAccaontIsLocked(true);
      } else if (data.CREATE_LINKED_LOGINS_CURRENT_LOGIN) { // 7
        handleFormError(`It looks like you’re already signed in to this account.
          Please sign in to the account you’d like to link below.`);
      } else if (data.CREATE_LINKED_LOGINS_REPETITION) { // 8
        handleFormError(`This account has already been linked.
          If you’d like to link another account, please sign in to that account below.`);
      } else if (data.NOT_PRO_ACCOUNT) {
        handleFormError('You can only link pro accounts at this time.');
      } else {
        setOpenSignInModel(false);
      }
      // #TODO: Need api endpoint to know how to handle success.
    }
    else {
      // #TODO: Add error modal for when network request fails.
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    const response = await handleResetPassword(resetUsername);
    const { data } = response;

    if (response.ok && isEmpty(data)) {
      setResetError([]);
      setResetSuccess(true);
    }

    if(response.status === 400) {
      setResetError(['Sorry, we couldn’t request a password reset. Please try again.']);
    }
    if (data.BAD_LOGIN) {
      setResetError([
        'We couldn’t find an account associated with this email address. Please check the email address entered or ',
        <a
          key="reset-link"
          href="http://help.aisleplanner.com/en/articles/1895151-resetting-your-aisle-planner-password"
          className="reset-password"
        >
          contact support
        </a>,
        ' for assistance.',
      ]);
    }
    if (data.ACCOUNT_CLOSED) {
      setResetError(['Your account is closed. To re-open, please contact support.']);
    }
    if (data.ACCOUNT_PLACEHOLDER) {
      setResetError(['You need to accept your email invite before logging in']);
    }
  };

  return (
    <>
      {!resetMode && (
        <div
          className={clsx('sign-in')}
        >
          <h1
            className="sign-in__title"
          >
            {signInTitle || 'Welcome back!'}
          </h1>
          <p
            className="sign-in__message"
          >
            {signInMessage || 'It\'s great to see you again.'}
          </p>
          <p
            className={clsx('sign-in__error', hasError && 'visible')}
          >
            {error}
          </p>
          <Form
            className={clsx('sign-in__form')}
            onSubmit={handleSubmit}
          >
            <InputField
              ref={usernameRef}
              placeholder="Email Address or Username"
              name="username"
              showLabel={false}
            />
            <FormPassword
              ref={passwordRef}
              placeholder="Password"
              name="password"
            />
            <FormSubmit
              value="Sign In"
              disabled={submitDisabled}
            />
          </Form>
          <div className="sign-in__forgot-password">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setResetMode(true);
              }}
            >
              {!accaontIsLocked ? (
                <p>Forgot your password?</p>
              ) : (
                <p>To access your account, click here to reset your password.</p>
              )}
            </a>
          </div>
        </div>
      )}

      {resetMode && (
        <div className="reset-form">
          <img src={logo} />
          {!resetSuccess && (
            <p>
              Enter your email or username and we'll send you an email
              with instructions on how to complete the password reset process.
            </p>
          )}

          {resetSuccess && (
            <p>
              If your email address is associated with an account in our system,
              we'll send you an email with instructions on how to update your password.
              Please check your email for next steps or contact support for assistance.
            </p>
          )}

          {resetError.length > 0 && (
            <p className="sign-in__error visible">
              {resetError}
            </p>
          )}

          {!resetSuccess && (
            <Form
              className={clsx('sign-in__form')}
              onSubmit={handleReset}
            >
              <InputField
                placeholder="Email Address or Username"
                name="username"
                showLabel={false}
                value={resetUsername}
                onChange={(e) => setResetUsername(e.target.value)}
              />
              <FormSubmit
                value="Send Reset Instructions"
                disabled={!resetUsername}
              />
              <button
                className="button--unstyled gold"
                onClick={() => setResetMode(false)}
              >
                Cancel
              </button>
            </Form>
          )}

          <div className="reset-form__footer">
            <button
              className="button--unstyled reset-form__footer-button"
              onClick={() => {
                setResetMode(false);
                setResetSuccess(false);
              }}
            >
              <i className="fa fa-long-arrow-left" />
              BACK TO SIGN IN
            </button>
            <a
              href="http://help.aisleplanner.com/en/articles/1895151-resetting-your-aisle-planner-password"
              target="_blank"
              className="button--unstyled reset-form__footer-button"
              rel="noreferrer"
            >
              SUPPORT
              <i className="fa fa-long-arrow-right" />
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default SignIn;
